import { useState, useRef } from "react";
import { Upload, X, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, resolveMedia, formatApiErrorDetail } from "@/lib/api";

/**
 * Drag & drop image picker. Accepts a single URL value; on upload it stores the
 * returned /api/uploads/... path. Also supports manual URL paste.
 */
export default function ImageUploader({ value, onChange, label, testid }) {
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Nuotrauka įkelta");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div className="space-y-3" data-testid={testid}>
      {label && <label className="overline text-[#5c5c5c]">{label}</label>}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`relative border-2 border-dashed transition-colors ${
          dragOver
            ? "border-[#a66e4b] bg-[#f9f8f6]"
            : "border-[#d8d4ce] bg-white"
        }`}
      >
        {value ? (
          <div className="flex gap-4 p-4 items-center">
            <div className="w-24 h-24 bg-[#f9f8f6] overflow-hidden flex-shrink-0">
              <img
                src={resolveMedia(value)}
                alt=""
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs text-[#5c5c5c] font-light break-all">{value}</div>
              <div className="mt-3 flex gap-3 text-[11px] uppercase tracking-[0.22em]">
                <button
                  type="button"
                  onClick={() => inputRef.current?.click()}
                  className="text-[#a66e4b] hover:text-[#d09a72]"
                >
                  Pakeisti
                </button>
                <button
                  type="button"
                  onClick={() => onChange("")}
                  className="text-[#5c5c5c] hover:text-[#e57373]"
                >
                  Pašalinti
                </button>
                <a
                  href={resolveMedia(value)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#5c5c5c] hover:text-[#1a1a1a] inline-flex items-center gap-1"
                >
                  Atidaryti <ExternalLink size={11} />
                </a>
              </div>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="w-full px-6 py-10 flex flex-col items-center gap-3 text-[#5c5c5c] hover:text-[#1a1a1a]"
          >
            <Upload size={28} strokeWidth={1.25} />
            <div className="text-sm font-light">
              Tempkite nuotrauką čia arba spustelėkite
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#7a7570]">
              JPG · PNG · WebP · iki 10 MB
            </div>
          </button>
        )}
        {busy && (
          <div className="absolute inset-0 bg-[#f9f8f6]/80 flex items-center justify-center text-sm text-[#a66e4b] tracking-[0.2em] uppercase">
            Įkeliama…
          </div>
        )}
      </div>

      <div className="flex gap-3 items-center">
        <input
          type="text"
          placeholder="Arba įklijuokite URL"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 bg-transparent border-b border-[#d8d4ce] focus:border-[#a66e4b] outline-none py-2 text-[#1a1a1a] font-light text-sm"
        />
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>
    </div>
  );
}
